import React, { useState, useEffect } from 'react';
import { FaRupeeSign, FaDownload, FaRedo, FaCalendarAlt } from 'react-icons/fa';
import { calculateIncomeTaxNewRegime, calculateIncomeTaxOldRegime, formatCurrency, getAvailableFinancialYears, TAX_SLABS_DATA } from '../utils/calculations';
import { generateIncomeTaxPDF } from '../utils/pdfGenerator';
import SEO from '../components/SEO';

const incomeTaxFaqs = [
  {
    question: 'Does this calculator compare old and new tax regime?',
    answer: 'Yes. This page calculates both regimes and helps you compare which option results in lower total tax for the selected financial year.',
  },
  {
    question: 'Can I include deductions like 80C, 80D, and HRA?',
    answer: 'Yes. When you switch to the old regime tab, you can enter common deductions such as Section 80C, Section 80D, HRA, and other eligible deductions.',
  },
  {
    question: 'Are cess and rebate included in the result?',
    answer: 'Yes. The calculator includes rebate where applicable and adds health and education cess to show the final estimated tax liability.',
  },
  {
    question: 'Is this tax calculator suitable for filing ITR directly?',
    answer: 'It is useful for planning and estimation, but you should still verify your final numbers with official tax forms, Form 16, AIS, and a qualified advisor if needed.',
  },
];

const IncomeTaxCalculator = () => {
  const [income, setIncome] = useState(1000000);
  const [regime, setRegime] = useState('new');
  const [financialYear, setFinancialYear] = useState('FY 2025-26');
  
  // Old regime deductions
  const [section80C, setSection80C] = useState(150000);
  const [section80D, setSection80D] = useState(25000);
  const [hra, setHra] = useState(0);
  const [otherDeductions, setOtherDeductions] = useState(0);
  
  const [resultNew, setResultNew] = useState(null);
  const [resultOld, setResultOld] = useState(null);

  const availableYears = getAvailableFinancialYears();

  /* eslint-disable react-hooks/exhaustive-deps */
  useEffect(() => {
    calculate();
  }, [income, section80C, section80D, hra, otherDeductions, financialYear]);
  /* eslint-enable react-hooks/exhaustive-deps */

  const calculate = () => {
    const newRegimeResult = calculateIncomeTaxNewRegime(income, financialYear);
    const oldRegimeResult = calculateIncomeTaxOldRegime(income, {
      section80C,
      section80D,
      hra,
      otherDeductions,
      standardDeduction: TAX_SLABS_DATA[financialYear]?.old?.standardDeduction || 50000,
    }, financialYear);
    
    setResultNew(newRegimeResult);
    setResultOld(oldRegimeResult);
  };

  const handleDownloadPDF = () => {
    const result = regime === 'new' ? resultNew : resultOld;
    generateIncomeTaxPDF(result, regime === 'new' ? 'New' : 'Old');
  };

  const handleReset = () => {
    setIncome(1000000);
    setSection80C(150000);
    setSection80D(25000);
    setHra(0);
    setOtherDeductions(0);
  };

  const getAssessmentYear = (fy) => {
    const startYear = parseInt(fy.split(' ')[1].split('-')[0]);
    return `AY ${startYear + 1}-${(startYear + 2).toString().slice(-2)}`;
  };

  const currentResult = regime === 'new' ? resultNew : resultOld;
  const betterRegime = resultNew && resultOld ? 
    (resultNew.totalTax <= resultOld.totalTax ? 'new' : 'old') : null;
  const savings = resultNew && resultOld ? 
    Math.abs(resultNew.totalTax - resultOld.totalTax) : 0;

  // Get current year's slabs for display
  const currentYearData = TAX_SLABS_DATA[financialYear];

  return (
    <div className="calculator-page fade-in">
      <SEO 
        title="Income Tax Calculator"
        description="Free Income Tax Calculator India FY 2025-26 - Calculate tax under Old & New regime. Compare tax slabs, deductions under 80C, 80D, HRA. Export tax computation PDF."
        keywords="income tax calculator, income tax calculator India, tax calculator FY 2025-26, old regime vs new regime, 80C deduction, 80D deduction, HRA exemption, tax slab calculator, income tax computation"
        canonicalPath="/income-tax"
        faqItems={incomeTaxFaqs}
      />
      <div className="calculator-header">
        <h1>Income Tax Calculator</h1>
        <p>Calculate income tax for {financialYear} ({getAssessmentYear(financialYear)}) under both regimes</p>
      </div>

      <div className="calculator-container">
        <div className="calculator-inputs">
          {/* Year Selection */}
          <div className="input-group">
            <label><FaCalendarAlt style={{ marginRight: '8px' }} />Select Financial Year</label>
            <select
              value={financialYear}
              onChange={(e) => setFinancialYear(e.target.value)}
              className="year-select"
              style={{
                width: '100%',
                padding: '12px 16px',
                fontSize: '16px',
                border: '2px solid var(--border)',
                borderRadius: '8px',
                background: 'var(--surface)',
                color: 'var(--text-primary)',
                cursor: 'pointer',
              }}
            >
              {availableYears.map((year) => (
                <option key={year} value={year}>
                  {year} ({getAssessmentYear(year)})
                </option>
              ))}
            </select>
          </div>

          <div className="tabs">
            <button 
              className={`tab ${regime === 'new' ? 'active' : ''}`}
              onClick={() => setRegime('new')}
            >
              New Regime
            </button>
            <button 
              className={`tab ${regime === 'old' ? 'active' : ''}`}
              onClick={() => setRegime('old')}
            >
              Old Regime
            </button>
          </div>

          <div className="input-group">
            <label>Annual Gross Income (₹)</label>
            <div className="input-with-icon">
              <FaRupeeSign className="icon" />
              <input
                type="number"
                value={income === '' ? '' : income}
                onChange={(e) => setIncome(e.target.value === '' ? '' : Number(e.target.value))}
                min="0"
                max="100000000"
              />
            </div>
            <div className="slider-container">
              <input
                type="range"
                min="0"
                max="5000000"
                step="50000"
                value={income || 0}
                onChange={(e) => setIncome(Number(e.target.value))}
                style={{ '--value': `${((income || 0) / 5000000) * 100}%` }}
              />
            </div>
          </div>

          {regime === 'old' && (
            <>
              <div className="input-group">
                <label>Section 80C (Max ₹1.5 lakh)</label>
                <div className="input-with-icon">
                  <FaRupeeSign className="icon" />
                  <input
                    type="number"
                    value={section80C}
                    onChange={(e) => setSection80C(Math.min(Number(e.target.value), 150000))}
                    min="0"
                    max="150000"
                  />
                </div>
                <small style={{ color: 'var(--text-secondary)' }}>
                  PPF, ELSS, LIC, etc.
                </small>
              </div>

              <div className="input-group">
                <label>Section 80D (Health Insurance)</label>
                <div className="input-with-icon">
                  <FaRupeeSign className="icon" />
                  <input
                    type="number"
                    value={section80D === '' ? '' : section80D}
                    onChange={(e) => setSection80D(e.target.value === '' ? '' : Number(e.target.value))}
                    min="0"
                    max="100000"
                  />
                </div>
                <small style={{ color: 'var(--text-secondary)' }}>
                  Self: ₹25k, Parents: ₹25k-50k
                </small>
              </div>

              <div className="input-group">
                <label>HRA Exemption (Annual)</label>
                <div className="input-with-icon">
                  <FaRupeeSign className="icon" />
                  <input
                    type="number"
                    value={hra === '' ? '' : hra}
                    onChange={(e) => setHra(e.target.value === '' ? '' : Number(e.target.value))}
                    min="0"
                  />
                </div>
              </div>

              <div className="input-group">
                <label>Other Deductions</label>
                <div className="input-with-icon">
                  <FaRupeeSign className="icon" />
                  <input
                    type="number"
                    value={otherDeductions === '' ? '' : otherDeductions}
                    onChange={(e) => setOtherDeductions(e.target.value === '' ? '' : Number(e.target.value))}
                    min="0"
                  />
                </div>
                <small style={{ color: 'var(--text-secondary)' }}>
                  80E, 80G, 80TTA, etc.
                </small>
              </div>
            </>
          )}

          <div className="button-group">
            <button className="btn btn-outline" onClick={handleReset}>
              <FaRedo style={{ marginRight: '8px' }} /> Reset
            </button>
            <button className="btn btn-secondary" onClick={handleDownloadPDF}>
              <FaDownload style={{ marginRight: '8px' }} /> Download PDF
            </button>
          </div>
        </div>

        <div className="calculator-results">
          {currentResult && (
            <>
              <div className="result-summary">
                <h3>Total Tax ({regime === 'new' ? 'New' : 'Old'} Regime)</h3>
                <div className="amount">{formatCurrency(currentResult.totalTax)}</div>
              </div>

              {betterRegime && (
                <div 
                  className="result-item" 
                  style={{ 
                    background: betterRegime === regime ? '#dcfce7' : '#fef3c7',
                    marginBottom: '16px',
                    borderRadius: '8px'
                  }}
                >
                  <span className="label">
                    {betterRegime === regime ? '✅ This is the better regime!' : '⚠️ Switch to save more'}
                  </span>
                  <span className="value" style={{ color: '#059669' }}>
                    Save {formatCurrency(savings)}
                  </span>
                </div>
              )}

              <div className="result-details">
                <div className="result-item">
                  <span className="label">Gross Income</span>
                  <span className="value">{formatCurrency(currentResult.grossIncome)}</span>
                </div>
                <div className="result-item">
                  <span className="label">
                    {regime === 'new' ? 'Standard Deduction' : 'Total Deductions'}
                  </span>
                  <span className="value">
                    -{formatCurrency(regime === 'new' ? currentResult.standardDeduction : currentResult.totalDeductions)}
                  </span>
                </div>
                <div className="result-item">
                  <span className="label">Taxable Income</span>
                  <span className="value">{formatCurrency(currentResult.taxableIncome)}</span>
                </div>
                <div className="result-item">
                  <span className="label">Tax Before Rebate</span>
                  <span className="value">{formatCurrency(currentResult.taxBeforeRebate)}</span>
                </div>
                {currentResult.rebate > 0 && (
                  <div className="result-item">
                    <span className="label">Rebate u/s 87A</span>
                    <span className="value" style={{ color: '#10b981' }}>
                      -{formatCurrency(currentResult.rebate)}
                    </span>
                  </div>
                )}
                <div className="result-item">
                  <span className="label">Health & Education Cess (4%)</span>
                  <span className="value">{formatCurrency(currentResult.cess)}</span>
                </div>
                <div className="result-item">
                  <span className="label">Effective Tax Rate</span>
                  <span className="value">{currentResult.effectiveRate}%</span>
                </div>
              </div>

              {currentResult.breakdown && currentResult.breakdown.length > 0 && (
                <div className="breakup-section">
                  <h4>Tax Slab Breakdown</h4>
                  <table className="tax-slab-table">
                    <thead>
                      <tr>
                        <th>Income Slab</th>
                        <th>Rate</th>
                        <th>Tax</th>
                      </tr>
                    </thead>
                    <tbody>
                      {currentResult.breakdown.map((item, index) => (
                        <tr key={index}>
                          <td>{item.slab}</td>
                          <td>{item.rate}</td>
                          <td>{formatCurrency(item.tax)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      <div className="info-box">
        <h4>Tax Slabs {financialYear}</h4>
        <div className="grid-2-cols" style={{ marginTop: '12px' }}>
          <div>
            <strong>New Regime {financialYear === 'FY 2025-26' ? '(Budget 2025)' : '(Default)'}</strong>
            <ul style={{ marginTop: '8px' }}>
              {currentYearData?.new?.slabs?.map((slab, index) => (
                <li key={index}>
                  {slab.max === Infinity 
                    ? `Above ₹${(slab.min / 100000).toFixed(1)}L: ${slab.rate}%`
                    : `₹${slab.min === 0 ? '0' : (slab.min / 100000).toFixed(1) + 'L'} - ₹${(slab.max / 100000).toFixed(1)}L: ${slab.rate === 0 ? 'Nil' : slab.rate + '%'}`
                  }
                </li>
              ))}
            </ul>
            <p style={{ marginTop: '8px' }}>Standard deduction: ₹{(currentYearData?.new?.standardDeduction || 75000).toLocaleString('en-IN')}</p>
            <p style={{ marginTop: '4px', fontSize: '0.9em', color: 'var(--text-secondary)' }}>
              Rebate u/s 87A: Up to ₹{((currentYearData?.new?.rebateLimit || 700000) / 100000)}L income
            </p>
          </div>
          <div>
            <strong>Old Regime</strong>
            <ul style={{ marginTop: '8px' }}>
              {currentYearData?.old?.slabs?.map((slab, index) => (
                <li key={index}>
                  {slab.max === Infinity 
                    ? `Above ₹${(slab.min / 100000).toFixed(1)}L: ${slab.rate}%`
                    : `₹${slab.min === 0 ? '0' : (slab.min / 100000).toFixed(1) + 'L'} - ₹${(slab.max / 100000).toFixed(1)}L: ${slab.rate === 0 ? 'Nil' : slab.rate + '%'}`
                  }
                </li>
              ))}
            </ul>
            <p style={{ marginTop: '8px' }}>Standard deduction: ₹{(currentYearData?.old?.standardDeduction || 50000).toLocaleString('en-IN')}</p>
            <p style={{ marginTop: '4px', fontSize: '0.9em', color: 'var(--text-secondary)' }}>
              Rebate u/s 87A: Up to ₹{((currentYearData?.old?.rebateLimit || 500000) / 100000)}L income
            </p>
          </div>
        </div>
        {financialYear === 'FY 2025-26' && (
          <div style={{ marginTop: '16px', padding: '12px', background: '#fef3c7', borderRadius: '8px', fontSize: '0.9em' }}>
            <strong>⚠️ Note:</strong> FY 2025-26 slabs are based on Budget 2025 proposals. Actual rates may vary pending final approval.
          </div>
        )}
      </div>

      <section className="info-box" style={{ marginTop: '24px' }}>
        <h4>Income Tax Calculator FAQs</h4>
        <div style={{ display: 'grid', gap: 12 }}>
          {incomeTaxFaqs.map((item) => (
            <details key={item.question} style={{ border: '1px solid #dbe3f0', borderRadius: 12, background: '#fff', padding: '14px 16px' }}>
              <summary style={{ cursor: 'pointer', fontWeight: 600, color: '#0f172a' }}>{item.question}</summary>
              <p style={{ margin: '12px 0 0', color: '#475569', lineHeight: 1.65 }}>{item.answer}</p>
            </details>
          ))}
        </div>
      </section>
    </div>
  );
};

export default IncomeTaxCalculator;
